/**
 * sl-jsx3.16.3: Groups Management Panel
 *
 * Lists all workspace groups with CRUD operations.
 * Auto-sync groups (All Members / All Guests) are read-only.
 */
import {
  Button,
  IconButton,
  Input,
  Loading,
  notify,
  useConfirmModal,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import {
  GroupApiService,
  type GroupData,
} from '@affine/core/modules/group-api';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { DeleteIcon, EditIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import {
  SettingsContentSection,
  SettingsContentSurface,
  SettingsContentText,
  SettingsNotice,
} from '../shared/settings-content';
import {
  SettingsBadge,
  SettingsEntityList,
  SettingsEntityMessage,
} from '../shared/settings-entity';
import { SettingsSearchableEntityPage } from '../shared/settings-entity-page';
import { SettingsEntityFormDialog } from '../shared/settings-form-dialog';
import { SettingsGroupExpandableItem } from '../shared/settings-identity';
import { useSettingsEntityList } from '../shared/use-settings-entity-list';
import { GroupMembersEditor } from './group-members-editor';

export const CloudGroupsPanel = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const groupApiService = useService(GroupApiService);
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;

  const {
    data: groups,
    isLoading,
    error,
    refresh: fetchGroups,
  } = useSettingsEntityList<GroupData>(
    () =>
      backendAvailable
        ? groupApiService.getGroups(workspaceId)
        : Promise.resolve([]),
    [backendAvailable, groupApiService, workspaceId]
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupData | null>(null);
  const [modalName, setModalName] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [modalSaving, setModalSaving] = useState(false);

  const handleOpenCreate = useCallback(() => {
    setEditingGroup(null);
    setModalName('');
    setModalDescription('');
    setModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((group: GroupData) => {
    setEditingGroup(group);
    setModalName(group.name);
    setModalDescription(group.description ?? '');
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingGroup(null);
    setModalName('');
    setModalDescription('');
  }, []);

  const handleModalSave = useAsyncCallback(async () => {
    const trimmedName = modalName.trim();
    if (!trimmedName) return;

    setModalSaving(true);
    try {
      if (editingGroup) {
        await groupApiService.updateGroup(workspaceId, editingGroup.id, {
          name: trimmedName,
          description: modalDescription.trim() || undefined,
        });
        notify.success({
          title: t['com.streamlain.settings.workspace.groups.update.success'](),
        });
      } else {
        await groupApiService.createGroup(
          workspaceId,
          trimmedName,
          modalDescription.trim() || undefined
        );
        notify.success({
          title: t['com.streamlain.settings.workspace.groups.create.success'](),
        });
      }
      handleModalClose();
      await fetchGroups();
    } catch (err) {
      notify.error({ title: UserFriendlyError.fromAny(err).message });
    } finally {
      setModalSaving(false);
    }
  }, [
    editingGroup,
    fetchGroups,
    groupApiService,
    handleModalClose,
    modalDescription,
    modalName,
    t,
    workspaceId,
  ]);

  const { openConfirmModal } = useConfirmModal();

  const handleDelete = useCallback(
    (group: GroupData) => {
      openConfirmModal({
        title:
          t['com.streamlain.settings.workspace.groups.delete.confirm.title'](),
        description: t[
          'com.streamlain.settings.workspace.groups.delete.confirm.description'
        ]({ name: group.name }),
        confirmText:
          t[
            'com.streamlain.settings.workspace.groups.delete.confirm.confirm'
          ](),
        cancelText: t['Cancel'](),
        confirmButtonOptions: { variant: 'error' },
        onConfirm: async () => {
          try {
            await groupApiService.deleteGroup(workspaceId, group.id);
            notify.success({
              title:
                t['com.streamlain.settings.workspace.groups.delete.success'](),
            });
            await fetchGroups();
          } catch (err) {
            notify.error({ title: UserFriendlyError.fromAny(err).message });
          }
        },
      });
    },
    [fetchGroups, groupApiService, openConfirmModal, t, workspaceId]
  );

  const filteredGroups = useMemo(() => {
    if (!groups) return [];
    if (!searchQuery.trim()) return groups;
    const q = searchQuery.toLowerCase();
    return groups.filter(
      group =>
        group.name.toLowerCase().includes(q) ||
        group.description?.toLowerCase().includes(q)
    );
  }, [groups, searchQuery]);

  const title = useMemo(() => {
    if (groups) {
      return `${t['com.streamlain.settings.workspace.groups.title']()} (${groups.length})`;
    }
    return t['com.streamlain.settings.workspace.groups.title']();
  }, [groups, t]);
  const showOfflineNotice = !backendAvailable;

  return (
    <>
      <SettingsSearchableEntityPage
        title={title}
        subtitle={t['com.streamlain.settings.workspace.groups.description']()}
        headerAction={<SettingsOfflineHeaderAction show={showOfflineNotice} />}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={
          t['com.streamlain.settings.workspace.groups.search']?.() ??
          'Search groups...'
        }
        action={
          <Button
            data-testid="settings-groups-create-button"
            variant="primary"
            onClick={handleOpenCreate}
            disabled={showOfflineNotice}
          >
            {t['com.streamlain.settings.workspace.groups.create']()}
          </Button>
        }
        loading={isLoading && groups === undefined && !showOfflineNotice}
        hasItems={filteredGroups.length > 0}
        loadingContent={
          <SettingsEntityMessage>
            <Loading size={20} />
            <span>
              {t['com.streamlain.settings.member.loading']?.() ?? 'Loading...'}
            </span>
          </SettingsEntityMessage>
        }
        emptyContent={
          error ? (
            <SettingsEntityMessage>
              {showOfflineNotice ? offlineLabel : error}
            </SettingsEntityMessage>
          ) : (
            <SettingsEntityMessage>
              {showOfflineNotice
                ? offlineLabel
                : searchQuery
                  ? (t[
                      'com.streamlain.settings.workspace.groups.empty-search'
                    ]?.() ?? 'No groups found matching your search.')
                  : t['com.streamlain.settings.workspace.groups.empty']()}
            </SettingsEntityMessage>
          )
        }
      >
        <SettingsEntityList>
          {filteredGroups.map(group => (
            <SettingsGroupExpandableItem
              key={group.id}
              dataTestId={`settings-group-row-${group.id}`}
              expanded={expandedGroupId === group.id}
              onToggle={() =>
                setExpandedGroupId(prev =>
                  prev === group.id ? null : group.id
                )
              }
              group={group}
              badges={
                group.isAutoSync ? (
                  <SettingsBadge>
                    {t[
                      'com.streamlain.settings.workspace.groups.badge.auto-sync'
                    ]()}
                  </SettingsBadge>
                ) : undefined
              }
              aside={
                !group.isAutoSync ? (
                  <>
                    <IconButton
                      data-testid={`settings-group-edit-${group.id}`}
                      aria-label={t['Edit']?.() ?? 'Edit'}
                      onClick={event => {
                        event.stopPropagation();
                        handleOpenEdit(group);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      data-testid={`settings-group-delete-${group.id}`}
                      aria-label={t['Delete']?.() ?? 'Delete'}
                      onClick={event => {
                        event.stopPropagation();
                        handleDelete(group);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                ) : undefined
              }
            >
              <SettingsContentSurface>
                {group.description ? (
                  <SettingsContentSection
                    title={
                      t[
                        'com.streamlain.settings.workspace.groups.details.title'
                      ]?.() ?? 'Description'
                    }
                  >
                    <SettingsContentText>
                      {group.description}
                    </SettingsContentText>
                  </SettingsContentSection>
                ) : null}

                {group.isAutoSync ? (
                  <SettingsNotice
                    title={
                      t[
                        'com.streamlain.settings.workspace.groups.auto-sync.title'
                      ]?.() ?? 'Automatically managed'
                    }
                  >
                    {t[
                      'com.streamlain.settings.workspace.groups.auto-sync.description'
                    ]?.() ??
                      'This group is managed automatically and cannot be edited manually.'}
                  </SettingsNotice>
                ) : null}

                <GroupMembersEditor group={group} />
              </SettingsContentSurface>
            </SettingsGroupExpandableItem>
          ))}
        </SettingsEntityList>
      </SettingsSearchableEntityPage>

      <SettingsEntityFormDialog
        open={modalOpen}
        onOpenChange={open => {
          if (!open) handleModalClose();
        }}
        title={
          editingGroup
            ? t['com.streamlain.settings.workspace.groups.edit.title']()
            : t['com.streamlain.settings.workspace.groups.create.title']()
        }
        onSave={handleModalSave}
        onCancel={handleModalClose}
        saveLabel={
          editingGroup
            ? t['Save']()
            : t['com.streamlain.settings.workspace.groups.create']()
        }
        cancelLabel={t['Cancel']()}
        saveDisabled={!modalName.trim()}
        saving={modalSaving}
        savingLabel={
          t['com.streamlain.settings.member.loading']?.() ?? 'Loading...'
        }
        saveTestId="settings-groups-modal-save-button"
      >
        <Input
          data-testid="settings-groups-modal-name-input"
          placeholder={t[
            'com.streamlain.settings.workspace.groups.modal.name.placeholder'
          ]()}
          value={modalName}
          onChange={setModalName}
          autoFocus
        />
        <Input
          data-testid="settings-groups-modal-description-input"
          placeholder={t[
            'com.streamlain.settings.workspace.groups.modal.description.placeholder'
          ]()}
          value={modalDescription}
          onChange={setModalDescription}
        />
      </SettingsEntityFormDialog>
    </>
  );
};
